/** Custom Hook 'useValidarActividad'
 * Descripción: Marca un servicio (actividad) como validado, registrando en el
 *   backend el usuario logueado que realiza la validación y la fecha.
 * Parámetros:
 *   @setData Función de useState que permite actualizar el estado de las actividades.
 * Return values:
 *   @validarServicio Función que se debe invocar para validar la actividad.
 *   @loadingId Id de la actividad que se está validando (para deshabilitar el botón).
 */

import { useCallback, useContext, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../global/global";
import { AuthContext } from "../context/AuthContext";
import { ActividadIndex } from "../interfaces/ActividadIndexInterface";

const useValidarActividad = (
  setData: React.Dispatch<React.SetStateAction<ActividadIndex[] | undefined>>
) => {
  const authContext = useContext(AuthContext);
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const validarServicio = useCallback(
    async (idActividad: number) => {
      if (
        window.confirm(
          "¿Está seguro que desea validar este servicio? Se registrará su usuario como validador."
        ) !== true
      ) {
        return;
      }

      setLoadingId(idActividad);
      try {
        const { data } = await axios.post(
          `${API}/actividad/validar/${idActividad}`,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + authContext.token,
            },
          }
        );

        // Actualiza la fila validada en el estado local
        setData((prev) =>
          prev?.map((element) =>
            element.id === idActividad
              ? {
                  ...element,
                  validado_por: data.validado_por?.id ?? null,
                  fecha_validacion: data.fecha_validacion ?? null,
                  validador: data.validado_por ?? null,
                }
              : element
          )
        );

        toast.success("Servicio validado con éxito", {
          position: "top-left",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
        });
      } catch (error: any) {
        const mensaje =
          error?.response?.status === 403
            ? "No tienes permiso para validar servicios"
            : "Ocurrió un error al validar el servicio";
        toast.error(mensaje, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
        });
      } finally {
        setLoadingId(null);
      }
    },
    [authContext.token, setData]
  );

  const desvalidarServicio = useCallback(
    async (idActividad: number) => {
      if (
        window.confirm(
          "¿Está seguro que desea quitar la validación de este servicio?"
        ) !== true
      ) {
        return;
      }

      setLoadingId(idActividad);
      try {
        await axios.post(
          `${API}/actividad/desvalidar/${idActividad}`,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + authContext.token,
            },
          }
        );

        // Limpia la validación de la fila en el estado local
        setData((prev) =>
          prev?.map((element) =>
            element.id === idActividad
              ? {
                  ...element,
                  validado_por: null,
                  fecha_validacion: null,
                  validador: null,
                }
              : element
          )
        );

        toast.success("Validación removida con éxito", {
          position: "top-left",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
        });
      } catch (error: any) {
        const mensaje =
          error?.response?.status === 403
            ? "Solo el usuario que validó puede desvalidar este servicio"
            : "Ocurrió un error al desvalidar el servicio";
        toast.error(mensaje, {
          position: "top-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
        });
      } finally {
        setLoadingId(null);
      }
    },
    [authContext.token, setData]
  );

  return { validarServicio, desvalidarServicio, loadingId };
};

export default useValidarActividad;